// Sorting a list page's rows.
//
// Every list table in the app sorts through here (round 34), which exists
// because the obvious implementation is wrong in two ways that both look fine
// until somebody uses them.
//
// ONE: STRINGIFYING EVERYTHING. Zanetto Tools' sort does
// `String(a[key]).localeCompare(...)` for every column, so its "Total Value"
// column orders 100, 1000, 25, 9 and its dates order by the day of the month
// they happen to start with. A size column, a count, a currency and a date all
// need to compare as what they are — so the caller says which, and gets a
// comparator that means it.
//
// TWO: WHERE BLANKS GO. A row with no category, no expiry, no registration is
// not "less than" the others — it is missing. Sorted as an empty string it
// clumps at the top ascending and the bottom descending, so reversing the sort
// moves rows that have nothing to say through the answer the reader is looking
// at. Here a blank sorts LAST in both directions, always. The resources page
// reached for the same idea with `?? 'zzzz'`, which works right up until a
// value legitimately starts with "zzzz"; this does it by asking whether the
// value is there rather than by inventing one that hopefully sorts late.
import { useState } from 'react';
import type { SortDirection } from '../components/ui/SortHeader';

/** What a column can sort on. `null`/`undefined`/`''` all mean "missing". */
export type SortValue = string | number | Date | null | undefined;

function isMissing(value: SortValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return Number.isNaN(value);
  return Number.isNaN(value.getTime());
}

/**
 * Compare two present values. Numbers and dates compare numerically; strings
 * compare with `localeCompare`, which is what puts "Ångström" where an
 * Australian reader expects it rather than after "Z".
 */
function comparePresent(a: SortValue, b: SortValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b), 'en-AU', { sensitivity: 'base', numeric: true });
}

/**
 * Sort a copy of `rows` by whatever `value` pulls off each one.
 *
 * The direction applies to the comparison ONLY — a missing value stays at the
 * bottom whichever way the arrow points, because "no expiry" is not a late
 * date and reversing the sort should not walk it through the rows somebody is
 * reading.
 */
export function sortRows<T>(
  rows: readonly T[],
  value: (row: T) => SortValue,
  dir: SortDirection
): T[] {
  return [...rows].sort((rowA, rowB) => {
    const a = value(rowA);
    const b = value(rowB);
    const aMissing = isMissing(a);
    const bMissing = isMissing(b);
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    const compared = comparePresent(a, b);
    return dir === 'asc' ? compared : -compared;
  });
}

/**
 * The state behind a set of `SortHeader`s.
 *
 * Clicking the column already sorted flips the direction; clicking a new one
 * starts it ascending, which is what every table in every other application
 * does and therefore the only thing that will not feel broken.
 *
 * Deliberately component state rather than a URL parameter. A filter belongs
 * in the URL — a manager who has narrowed to "In progress" can bookmark it or
 * send it to somebody (round 33) — but which column somebody clicked while
 * scanning a list is not a view worth naming, and putting it in the query
 * string would leave `?sort=` on every link they copy out of the app.
 */
export function useSort<K extends string>(initialKey: K, initialDir: SortDirection = 'asc') {
  const [sortKey, setSortKey] = useState<K>(initialKey);
  const [sortDir, setSortDir] = useState<SortDirection>(initialDir);

  const onSort = (key: K) => {
    if (key === sortKey) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return { sortKey, sortDir, onSort };
}
